import { map } from 'lodash/fp';
import get from 'lodash/fp/get';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import { createSlice } from '@reduxjs/toolkit';

import { epochToDateInReadableFormat, epochToTimeInReadableFormat } from '../../utils';

import { USER_STATUS } from './UsersConstants';

const EMPLOYEE_DOCUMENTATION_ERROR_FALLBACK = { documents: [], count: 0 };
const ONBOARDING_DOCUMENTS_ERROR_FALLBACK = null;

const getProfilePictureResponseDocumentId = (action) => action?.payload?.documentId || null;

const initialState = {
  users: [],
  candidateAttendance: [],
  isLoadingCandidateAttendance: false,
  totalRowCount: 0,
  isUsersLoading: false,
  isUpdateProcessing: false,
  isUpdateUserNameProcessing: false,
  isUpdateUserPhoneNumberProcessing: false,
  isUpdateUserAddressProcessing: false,
  isUserDetailLoading: false,
  hasUpdated: null,
  workerPlacements: null,
  isLoadingWorkerPlacements: false,
  pendingWorkerPlacements: null,
  isLoadingPendingWorkerPlacements: false,
  isUpdatingInfraction: false,
  reliabilityExclusionPlacementId: null,
  shouldReloadCandidateAttendance: false,
  pendingOffers: null,
  isLoadingPendingOffers: false,
  workerContracts: null,
  isLoadingWorkerContracts: false,
  employeeDocumentation: null,
  isLoadingEmployeeDocumentation: false,
  hasEmployeeDocumentationError: false,
  onboardingDocuments: null,
  isLoadingOnboardingDocuments: false,
  hasOnboardingDocumentsError: false,
  isProcessingUserStatusUpdate: false,
  isProcessingDocumentsApproved: false,
  isProcessingEVerify: false,
  taxCreditOpportunities: {},
  isProcessingWotcEligibility: false,
  isProcessingUrbanYouthCertified: false,
  certifications: [],
  isLoadingCertifications: false,
  DNRData: {},
  DNAData: {},
  isLoadingUpdateDNA: false,
  isLoadingUpdateDNR: false,
  isLoadingUserHistory: false,
  selectedUpcomingCount: 0,
  isProcessingPostUser: false,
  postUserError: null,
  userProfilePictureUrl: null,
  fetchedProfilePictureId: null,
};

export const usersListSlice = createSlice({
  name: 'usersList',
  initialState,
  reducers: {
    /* eslint-disable no-param-reassign */
    getUsers: (state, action) => {
      if (action?.payload?.page === 0) {
        state.users = [];
      }
      state.isUsersLoading = true;
    },
    getUsersProcessed: (state, action) => {
      state.isUsersLoading = false;
      const { count, documents } = action.payload.data;
      const mappedUsers = map((user) => {
        const { _id, id, name, role, metadata, recruiter, candidate, email, account, status } =
          user;
        const formatLastLogin = (timestamp) => {
          if (!timestamp) return null;
          const date = epochToDateInReadableFormat(timestamp);
          const time = epochToTimeInReadableFormat(timestamp);
          return `${date} at ${time}`;
        };

        return {
          account,
          id: id || _id,
          name,
          role,
          externalId: get(['bullhorn', 'id'], metadata),
          email,
          candidate,
          recruiter,
          phoneNumber: get(['phone', 'phoneNumber'], user),
          status,
          state: user.state,
          location: 'Location placeholder',
          rating: user?.rating,
          lastLoginDate: formatLastLogin(user?.lastLoginDate),
          corporationName:
            user?.role === UserRole.EMPLOYER ? user?.employer?.corporation?.name : null,
        };
      }, documents);
      state.users.push(...mappedUsers);
      state.totalRowCount = count;
    },

    getUsersError: (state) => {
      state.isUsersLoading = false;
      state.totalRowCount = 0;
      state.users = [];
    },
    postRecrutierAgencyUpdate: (state) => {
      state.isUpdateProcessing = true;
    },

    postRecrutierAgencyUpdateProcessed: (state) => {
      state.isUpdateProcessing = false;
    },

    postRecrutierAgencyUpdateError: (state) => {
      state.isUpdateProcessing = false;
    },
    postCandidateAgencyUpdate: (state) => {
      state.isUpdateProcessing = true;
    },

    postCandidateAgencyUpdateProcessed: (state) => {
      state.isUpdateProcessing = false;
    },

    postCandidateAgencyUpdateError: (state) => {
      state.isUpdateProcessing = false;
    },

    getUserDetail: (state, action) => {
      if (action?.payload?.page === 0) {
        state.users = [];
      }
      state.isUserDetailLoading = true;
    },
    getUserDetailProcessed: (state, action) => {
      state.isUserDetailLoading = false;
      const { documents } = action.payload.data;
      const mappedUsers = map((user) => {
        const { _id, id, name, role, metadata, recruiter, candidate, email, account, status } =
          user;

        const formatLastLogin = (timestamp) => {
          if (!timestamp) return null;
          const date = epochToDateInReadableFormat(timestamp);
          const time = epochToTimeInReadableFormat(timestamp);
          return `${date} at ${time}`;
        };
        const profilePictureId = user.profilePictureId || null;
        const profilePictureUrl =
          profilePictureId && profilePictureId === state.fetchedProfilePictureId
            ? state.userProfilePictureUrl
            : null;
        return {
          account,
          id: id || _id,
          name,
          role,
          externalId: get(['bullhorn', 'id'], metadata),
          email,
          candidate,
          recruiter,
          phoneNumber: get(['phone', 'phoneNumber'], user),
          status,
          state: user.state,
          location: 'Location placeholder',
          rating: user?.rating,
          lastLoginDate: formatLastLogin(user?.lastLoginDate),
          corporationName:
            user?.role === UserRole.EMPLOYER ? user?.employer?.corporation?.name : null,
          profilePictureId,
          profilePictureUrl,
        };
      }, documents);
      state.userDetail = mappedUsers;
    },

    getUserDetailError: (state) => {
      state.isUserDetailLoading = false;
    },
    setUserDetail: (state, action) => {
      state.userDetail = action.payload;
    },
    getUserProfilePicture: (state, action) => {
      const documentId = action?.payload?.documentId || null;
      if (state.fetchedProfilePictureId !== documentId) {
        state.userProfilePictureUrl = null;
      }
      state.fetchedProfilePictureId = documentId;
    },
    getUserProfilePictureProcessed: (state, action) => {
      const responseDocumentId = getProfilePictureResponseDocumentId(action);
      if (!responseDocumentId || responseDocumentId !== state.fetchedProfilePictureId) {
        return;
      }
      const presignedUrl = action?.payload?.data?.presignedUrl || null;
      state.userProfilePictureUrl = presignedUrl;
      if (Array.isArray(state.userDetail) && state.userDetail[0]) {
        const currentId = state.userDetail[0].profilePictureId || null;
        if (currentId && currentId === responseDocumentId) {
          state.userDetail[0].profilePictureUrl = presignedUrl;
        }
      }
    },
    getUserProfilePictureError: (state, action) => {
      const responseDocumentId = getProfilePictureResponseDocumentId(action);
      if (!responseDocumentId || responseDocumentId !== state.fetchedProfilePictureId) {
        return;
      }
      state.userProfilePictureUrl = null;
      if (Array.isArray(state.userDetail) && state.userDetail[0]) {
        state.userDetail[0].profilePictureUrl = null;
      }
    },
    clearUserProfilePicture: (state) => {
      state.userProfilePictureUrl = null;
      state.fetchedProfilePictureId = null;
      if (Array.isArray(state.userDetail) && state.userDetail[0]) {
        state.userDetail[0].profilePictureUrl = null;
      }
    },
    updateUser: (state) => {
      state.isUpdateProcessing = true;
    },
    updateUserProcessed: (state, action) => {
      state.hasUpdated = action.payload.data.id;
      state.isUpdateProcessing = false;
    },
    updateUserError: (state) => {
      state.isUpdateProcessing = false;
    },
    updateUserName: (state) => {
      state.isUpdateUserNameProcessing = true;
    },
    updateUserNameProcessed: (state, action) => {
      state.isUpdateUserNameProcessing = false;
      const response = action?.payload?.data?.user;
      const userId = response?._id;
      const name = response?.name;

      if (!userId) return;

      // Update user in userDetail if it exists
      if (state?.userDetail) {
        const userDetailIndex = state.userDetail?.findIndex((user) => user?.id === userId);
        if (userDetailIndex !== -1) {
          state.userDetail[userDetailIndex].name = name;
        }
      }
    },
    updateUserNameError: (state) => {
      state.isUpdateUserNameProcessing = false;
    },
    updateUserPhoneNumber: (state) => {
      state.isUpdateUserPhoneNumberProcessing = true;
    },
    updateUserPhoneNumberProcessed: (state, action) => {
      state.isUpdateUserPhoneNumberProcessing = false;
      const response = action?.payload?.data;
      const userId = response?.userId;
      const phoneNumber = response?.phoneNumber;
      if (!userId) return;
      // Update user in userDetail if it exists
      if (state?.userDetail) {
        const userDetailIndex = state.userDetail?.findIndex((user) => user?.id === userId);
        if (userDetailIndex !== -1) {
          state.userDetail[userDetailIndex].phoneNumber = phoneNumber;
        }
      }
    },
    updateUserPhoneNumberError: (state) => {
      state.isUpdateUserPhoneNumberProcessing = false;
    },
    updateUserAddress: (state) => {
      state.isUpdateUserAddressProcessing = true;
    },
    updateUserAddressProcessed: (state, action) => {
      state.isUpdateUserAddressProcessing = false;
      const response = action?.payload?.data;
      const userId = response?.userId;
      const address = response?.address;

      if (!userId) return;

      // Update user in userDetail if it exists
      if (state?.userDetail) {
        const userDetailIndex = state.userDetail?.findIndex((user) => user?.id === userId);
        if (userDetailIndex !== -1) {
          const user = state.userDetail[userDetailIndex];
          // Ensure candidate object exists before accessing preferredLocation
          if (!user?.candidate) {
            user.candidate = {};
          }
          user.candidate.preferredLocation = address;
        }
      }
    },
    updateUserAddressError: (state) => {
      state.isUpdateUserAddressProcessing = false;
    },
    deactivateUser: (state) => {
      state.isProcessingUserStatusUpdate = true;
    },
    deactivateUserProcessed: (state, action) => {
      state.isProcessingUserStatusUpdate = false;
      const id = action?.payload?.data?.id;
      if (!id) return;

      if (Array.isArray(state.userDetail)) {
        state.userDetail = state.userDetail.map((user) =>
          user?.id === id ? { ...user, status: USER_STATUS.DEACTIVATED } : user
        );
      }
    },
    deactivateUserError: (state) => {
      state.isProcessingUserStatusUpdate = false;
    },
    reactivateUser: (state) => {
      state.isProcessingUserStatusUpdate = true;
    },
    reactivateUserProcessed: (state, action) => {
      state.isProcessingUserStatusUpdate = false;
      const status = action?.payload?.data?.user?.state?.status;
      const id = action?.payload?.data?.user?.id;
      if (!status || !id) return;

      if (Array.isArray(state.userDetail)) {
        state.userDetail = state.userDetail.map((user) =>
          user?.id === id ? { ...user, status } : user
        );
      }
    },
    reactivateUserError: (state) => {
      state.isProcessingUserStatusUpdate = false;
    },
    getWorkerPlacements: (state) => {
      state.isLoadingWorkerPlacements = true;
    },
    getWorkerPlacementsProcessed: (state, action) => {
      state.workerPlacements = action.payload.data;
      state.isLoadingWorkerPlacements = false;
    },
    getWorkerPlacementsError: (state) => {
      state.isLoadingWorkerPlacements = false;
    },
    getCandidateAttendance: (state) => {
      state.isLoadingCandidateAttendance = true;
      state.shouldReloadCandidateAttendance = false;
    },
    getCandidateAttendanceProcessed: (state, action) => {
      state.isLoadingCandidateAttendance = false;
      state.candidateAttendance = action?.payload?.data;
    },
    getCandidateAttendanceError: (state, action) => {
      state.isLoadingCandidateAttendance = false;
      state.candidateAttendance = action?.payload?.data;
    },

    updateInfraction: (state) => {
      state.isUpdatingInfraction = true;
    },
    updateInfractionProcessed: (state) => {
      state.isUpdatingInfraction = false;
      state.shouldReloadCandidateAttendance = true;
    },
    updateInfractionError: (state) => {
      state.isUpdatingInfraction = false;
    },

    setReliabilityExclusion: (state, action) => {
      state.reliabilityExclusionPlacementId = action.payload?.placementId || null;
    },
    setReliabilityExclusionProcessed: (state, action) => {
      state.reliabilityExclusionPlacementId = null;
      const document = action?.payload?.data?.document;
      const id = document?._id || document?.id;
      if (!id || !Array.isArray(state.workerPlacements?.documents)) return;

      const excluded = document?.flags?.isExcludedFromReliability === true;
      state.workerPlacements.documents = state.workerPlacements.documents.map((placement) => {
        if (String(placement?._id) !== String(id)) return placement;
        return {
          ...placement,
          flags: {
            ...(placement.flags || {}),
            isExcludedFromReliability: excluded,
          },
        };
      });
    },
    setReliabilityExclusionError: (state) => {
      state.reliabilityExclusionPlacementId = null;
    },

    getPendingWorkerPlacements: (state) => {
      state.isLoadingPendingWorkerPlacements = true;
    },
    getPendingWorkerPlacementsProcessed: (state, action) => {
      state.pendingWorkerPlacements = action.payload.data;
      state.isLoadingPendingWorkerPlacements = false;
    },
    getPendingWorkerPlacementsError: (state) => {
      state.isLoadingPendingWorkerPlacements = false;
    },
    getPendingOffers: (state) => {
      state.isLoadingPendingOffers = true;
    },
    getPendingOffersProcessed: (state, action) => {
      state.pendingOffers = action.payload.data;
      state.isLoadingPendingOffers = false;
    },
    getPendingOffersError: (state) => {
      state.isLoadingPendingOffers = false;
    },
    getWorkerContracts: (state) => {
      state.isLoadingWorkerContracts = true;
    },
    getWorkerContractsProcessed: (state, action) => {
      state.workerContracts = action?.payload?.data || { documents: [], count: 0 };
      state.isLoadingWorkerContracts = false;
    },
    getWorkerContractsError: (state) => {
      state.isLoadingWorkerContracts = false;
    },
    getEmployeeDocumentation: (state) => {
      state.isLoadingEmployeeDocumentation = true;
      state.hasEmployeeDocumentationError = false;
      state.employeeDocumentation = null;
    },
    getEmployeeDocumentationProcessed: (state, action) => {
      state.employeeDocumentation = action?.payload?.data || { documents: [], count: 0 };
      state.isLoadingEmployeeDocumentation = false;
      state.hasEmployeeDocumentationError = false;
    },
    getEmployeeDocumentationError: (state) => {
      state.isLoadingEmployeeDocumentation = false;
      state.employeeDocumentation = EMPLOYEE_DOCUMENTATION_ERROR_FALLBACK;
      state.hasEmployeeDocumentationError = true;
    },
    getOnboardingDocuments: (state) => {
      state.isLoadingOnboardingDocuments = true;
      state.hasOnboardingDocumentsError = false;
      state.onboardingDocuments = null;
    },
    getOnboardingDocumentsProcessed: (state, action) => {
      state.onboardingDocuments = action?.payload?.data || {};
      state.isLoadingOnboardingDocuments = false;
      state.hasOnboardingDocumentsError = false;
    },
    getOnboardingDocumentsError: (state) => {
      state.isLoadingOnboardingDocuments = false;
      state.onboardingDocuments = ONBOARDING_DOCUMENTS_ERROR_FALLBACK;
      state.hasOnboardingDocumentsError = true;
    },
    updateDocumentsApproved: (state) => {
      state.isProcessingDocumentsApproved = true;
    },
    updateDocumentsApprovedProcessed: (state, action) => {
      state.isProcessingDocumentsApproved = false;
      const { _id, status, candidate } = action?.payload?.data?.user || {};
      if (_id && Array.isArray(state.userDetail)) {
        state.userDetail = state.userDetail.map((user) =>
          user?.id === _id
            ? {
                ...user,
                ...(status !== undefined && { status }),
                candidate: { ...user?.candidate, onboardingDoc: candidate?.onboardingDoc },
              }
            : user
        );
      }
    },
    updateDocumentsApprovedError: (state) => {
      state.isProcessingDocumentsApproved = false;
    },

    updateEVerify: (state) => {
      state.isProcessingEVerify = true;
    },
    updateEVerifyProcessed: (state, action) => {
      state.isProcessingEVerify = false;
      const { _id, status, candidate } = action?.payload?.data?.user || {};
      if (_id && Array.isArray(state.userDetail)) {
        state.userDetail = state.userDetail.map((user) =>
          user?.id === _id
            ? {
                ...user,
                ...(status !== undefined && { status }),
                candidate: { ...user?.candidate, eVerify: candidate?.eVerify },
              }
            : user
        );
      }
    },
    updateEVerifyError: (state) => {
      state.isProcessingEVerify = false;
    },

    updateWotcEligibility: (state) => {
      state.isProcessingWotcEligibility = true;
    },
    updateWotcEligibilityProcessed: (state, action) => {
      state.isProcessingWotcEligibility = false;
      const { candidate } = action?.payload?.data?.document || {};
      const isEligible = candidate?.taxCreditOpportunities?.wotc?.isEligible;
      if (isEligible !== undefined) {
        state.taxCreditOpportunities = {
          ...state.taxCreditOpportunities,
          isWotcEligible: !!isEligible,
        };
      }
    },
    updateWotcEligibilityError: (state) => {
      state.isProcessingWotcEligibility = false;
    },

    updateUrbanYouthCertified: (state) => {
      state.isProcessingUrbanYouthCertified = true;
    },
    updateUrbanYouthCertifiedProcessed: (state, action) => {
      state.isProcessingUrbanYouthCertified = false;
      const { candidate } = action?.payload?.data?.document || {};
      const isCertified = candidate?.taxCreditOpportunities?.nyUrbanYouth?.isCertified;
      if (isCertified !== undefined) {
        state.taxCreditOpportunities = {
          ...state.taxCreditOpportunities,
          isUrbanYouthCertified: !!isCertified,
        };
      }
    },
    updateUrbanYouthCertifiedError: (state) => {
      state.isProcessingUrbanYouthCertified = false;
    },

    getUserHistory: (state) => {
      state.isLoadingUserHistory = true;
    },
    getUserHistoryProcessed: (state, action) => {
      state.isLoadingUserHistory = false;
      const rawTaxCredits = action.payload.data?.documents[0]?.taxCreditOpportunities || {};
      state.taxCreditOpportunities = {
        isWotcEligible: !!rawTaxCredits?.isWotcEligible,
        isNyuytcEligible: !!rawTaxCredits?.isNyuytcEligible,
        isUrbanYouthCertified: !!rawTaxCredits?.isUrbanYouthCertified,
      };
      const { documents } = action.payload.data;
      const upcomingJobsCount = get('upcomingJobs', documents[0]) || 0;
      const DNRDetails = get(['DNRSummary', 'details'], documents[0]) || '';
      const DNADetails = get(['DNASummary', 'details'], documents[0]) || '';
      const dnrCount = get(['DNRSummary', 'count'], documents[0]);
      const selectedDNRDetails = map(
        (detail) => ({
          id: detail?.DNR?.time,
          corpId: detail?.corporation_id,
          corpName: detail?.corporation_name,
          reason: detail?.DNR?.reason,
          requester: detail?.DNR?.requester?.name,
          note: detail?.DNR?.note,
          timestamp: detail?.DNR?.timestamp,
        }),
        DNRDetails
      );

      const selectedDNADetails = [
        {
          id: DNADetails?.time,
          reason: get('reason', DNADetails),
          requester: get(['requester', 'name'], DNADetails),
          date: epochToDateInReadableFormat(get('time', DNADetails)),
        },
      ];

      state.DNRData = {
        type: 'DNR',
        count: dnrCount || 0,
        details: selectedDNRDetails || [],
      };

      state.DNAData = {
        type: 'DNA',
        count: get('id', selectedDNADetails[0]) ? 1 : 0,
        details: get('id', selectedDNADetails[0]) ? selectedDNADetails : [],
      };

      state.selectedUpcomingCount = upcomingJobsCount;
    },
    getUserHistoryError: (state) => {
      state.isLoadingUserHistory = false;
    },

    getCertificationsForUsers: (state) => {
      state.isLoadingCertifications = true;
    },
    getCertificationsForUsersProcessed: (state, action) => {
      const documents = get(['payload', 'data', 'documents'], action);
      const certifications = map((certification) => {
        const { _id } = certification;
        return {
          id: _id,
          ...certification,
        };
      }, documents);
      state.certifications = certifications;
      state.isLoadingCertifications = false;
    },
    getCertificationsForUsersError: (state) => {
      state.isLoadingCertifications = false;
    },

    updateDNA: (state) => {
      state.isLoadingUpdateDNA = true;
    },
    updateDNAProcessed: (state) => {
      state.isLoadingUpdateDNA = false;
    },
    updateDNAError: (state) => {
      state.isLoadingUpdateDNA = false;
    },

    updateDNR: (state) => {
      state.isLoadingUpdateDNR = true;
    },
    updateDNRProcessed: (state) => {
      state.isLoadingUpdateDNR = false;
    },
    updateDNRError: (state) => {
      state.isLoadingUpdateDNR = false;
    },

    postUser: (state) => {
      state.isProcessingPostUser = true;
    },
    postEmployer: (state) => {
      state.isProcessingPostUser = true;
    },
    postUserProcessed: (state) => {
      state.isProcessingPostUser = false;
    },
    postUserError: (state, action) => {
      state.isProcessingPostUser = false;
      const messageData =
        get(['payload', 'GLOBAL', 'messageKey'], action) || 'error.generic.something.went.wrong';
      state.postUserError = messageData;
    },
    postBullhornEmployers: (state) => {
      state.isProcessingPostUser = true;
    },
    postBullhornEmployersProcessed: (state) => {
      state.isProcessingPostUser = false;
    },
    postBullhornEmployersError: (state, action) => {
      state.isProcessingPostUser = false;
      const messageData =
        get(['payload', 'GLOBAL', 'messageKey'], action) || 'error.generic.something.went.wrong';
      state.postUserError = messageData;
    },
    clearPostUserError: (state) => {
      state.postUserError = null;
    },
  },
});

export const usersListReducer = usersListSlice.reducer;

export const {
  getUsers,
  getUsersError,
  getUserDetail,
  getUserDetailProcessed,
  getUserDetailError,
  getUsersProcessed,
  updateUser,
  updateUserProcessed,
  updateUserError,
  updateUserName,
  updateUserNameProcessed,
  updateUserNameError,
  updateUserPhoneNumber,
  updateUserPhoneNumberProcessed,
  updateUserPhoneNumberError,
  updateUserAddress,
  updateUserAddressProcessed,
  updateUserAddressError,
  deactivateUser,
  deactivateUserProcessed,
  deactivateUserError,
  reactivateUser,
  reactivateUserProcessed,
  reactivateUserError,
  postRecrutierAgencyUpdate,
  postRecrutierAgencyUpdateProcessed,
  postRecrutierAgencyUpdateError,
  postCandidateAgencyUpdate,
  postCandidateAgencyUpdateProcessed,
  postCandidateAgencyUpdateError,
  setUserDetail,
  getUserProfilePicture,
  getUserProfilePictureProcessed,
  getUserProfilePictureError,
  clearUserProfilePicture,
  updateUserCards,
  getWorkerPlacements,
  getWorkerPlacementsProcessed,
  getWorkerPlacementsError,
  getCandidateAttendance,
  getCandidateAttendanceProcessed,
  getCandidateAttendanceError,
  updateInfraction,
  updateInfractionProcessed,
  updateInfractionError,
  setReliabilityExclusion,
  setReliabilityExclusionProcessed,
  setReliabilityExclusionError,
  getPendingWorkerPlacements,
  getPendingWorkerPlacementsProcessed,
  getPendingWorkerPlacementsError,
  getPendingOffers,
  getPendingOffersProcessed,
  getPendingOffersError,
  getWorkerContracts,
  getWorkerContractsProcessed,
  getWorkerContractsError,
  getEmployeeDocumentation,
  getEmployeeDocumentationProcessed,
  getEmployeeDocumentationError,
  getOnboardingDocuments,
  getOnboardingDocumentsProcessed,
  getOnboardingDocumentsError,
  updateDocumentsApproved,
  updateDocumentsApprovedProcessed,
  updateDocumentsApprovedError,
  updateEVerify,
  updateEVerifyProcessed,
  updateEVerifyError,
  updateWotcEligibility,
  updateWotcEligibilityProcessed,
  updateWotcEligibilityError,
  updateUrbanYouthCertified,
  updateUrbanYouthCertifiedProcessed,
  updateUrbanYouthCertifiedError,
  getUserHistory,
  getUserHistoryProcessed,
  getUserHistoryError,
  getCertificationsForUsers,
  getCertificationsForUsersProcessed,
  getCertificationsForUsersError,
  updateDNA,
  updateDNAProcessed,
  updateDNAError,
  updateDNR,
  updateDNRProcessed,
  updateDNRError,
  postUser,
  postEmployer,
  postUserProcessed,
  postUserError,
  postBullhornEmployers,
  postBullhornEmployersProcessed,
  postBullhornEmployersError,
  clearPostUserError,
} = usersListSlice.actions;
